import { useState } from "react";
import {
  Loader2, TriangleAlert, UserRound, Check, Ban, History, Clock3, RefreshCw, Play, Archive,
} from "lucide-react";
import { Panel } from "./ui";
import { nexsysApi } from "./api";

const fmt = (t) => (t || "").slice(0, 16).replace("T", " ");

const PRIO_CLS = {
  CRITICAL: "text-rose-300 border-rose-400/40 bg-rose-400/10",
  HIGH: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  MEDIUM: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10",
  LOW: "text-white/50 border-white/15 bg-white/[0.04]",
};
const STATUS_CLS = {
  UNASSIGNED: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  ASSIGNED: "text-indigo-300 border-indigo-400/30 bg-indigo-400/10",
  UNDER_REVIEW: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
  RESOLVED: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  CLOSED: "text-white/40 border-white/15 bg-white/[0.04]",
};

function Badge({ cls, children, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] tracking-widest ${cls}`} data-testid={testid}>
      {children}
    </span>
  );
}

function Aging({ hours }) {
  if (hours == null) return null;
  const label = hours < 1 ? `${Math.round(hours * 60)}m` : hours < 48 ? `${Math.round(hours)}h` : `${Math.round(hours / 24)}d`;
  const cls = hours > 72 ? "text-rose-300 border-rose-400/40 bg-rose-400/10"
    : hours > 24 ? "text-amber-300 border-amber-400/30 bg-amber-400/10"
    : "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  return (
    <Badge cls={cls} testid="escalation-aging">
      <Clock3 className="h-2.5 w-2.5" /> AGING {label}
    </Badge>
  );
}

function Row({ k, v, tone }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-[118px] shrink-0 text-white/35">{k}</span>
      <span className={`min-w-0 break-words ${tone || "text-white/75"}`}>{v ?? "—"}</span>
    </div>
  );
}

function EscalationItem({ e, reviewers, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [reason, setReason] = useState("");
  const done = e.status === "RESOLVED" || e.status === "CLOSED";

  const run = async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      if (okText) setMsg({ tone: "ok", text: okText });
      onChanged();
    } catch (err) {
      const s = String(err?.message || "");
      setMsg({
        tone: "warn",
        text: s.includes("409") ? "State or escalation changed since review — refreshed, please reevaluate."
          : s.includes("400") ? "Rejected — check required reason/justification or workflow rules."
          : "Action failed.",
      });
      onChanged();
    } finally { setBusy(false); }
  };

  const resolve = (action) => {
    if (action === "dismiss" && !reason.trim()) {
      setMsg({ tone: "warn", text: "Dismissal requires a reason." });
      return;
    }
    run(
      () => nexsysApi.gatewayResolveConflict(e.id, action, reason.trim(), e.current_state_version),
      action === "dismiss" ? "Dismissed with reason — escalation RESOLVED, lineage preserved."
        : "Accepted as evidence via Conflict Resolution Workflow — escalation RESOLVED, governed state unchanged.",
    );
  };

  return (
    <div className={`rounded-xl border px-4 py-3 ${done ? "border-white/[0.07] bg-white/[0.015] opacity-70" : "border-fuchsia-400/20 bg-fuchsia-500/[0.04]"}`} data-testid="escalation-item">
      <div className="flex flex-wrap items-center gap-2">
        <TriangleAlert className={`h-3.5 w-3.5 shrink-0 ${done ? "text-white/30" : "text-fuchsia-300"}`} />
        <span className="font-mono text-[10.5px] text-white/85" data-testid="escalation-id">{(e.interaction_id || e.id).slice(0, 22)}</span>
        <Badge cls={PRIO_CLS[e.effective_priority] || PRIO_CLS.MEDIUM} testid="escalation-priority">{e.effective_priority}</Badge>
        {e.effective_priority !== e.priority && (
          <span className="font-mono text-[8.5px] text-amber-300/70">↑ aging policy (set: {e.priority})</span>
        )}
        <Badge cls={STATUS_CLS[e.status] || STATUS_CLS.UNASSIGNED} testid="escalation-status">{e.status}</Badge>
        {!done && <Aging hours={e.aging_hours} />}
        {e.state_changed_since_escalation && !done && (
          <Badge cls="text-rose-300 border-rose-400/40 bg-rose-400/10" testid="escalation-reevaluate">
            <RefreshCw className="h-2.5 w-2.5" /> STATE CHANGED — REEVALUATE
          </Badge>
        )}
      </div>

      <div className="mt-2 grid gap-x-8 gap-y-1 font-mono text-[10px] lg:grid-cols-2">
        <Row k="Assertion" v={e.assertion || "—"} tone="text-rose-200/90" />
        <Row k="Governed value" v={(e.governed_context || [])[0] || "—"} tone="text-cyan-200/85" />
        <Row k="Entity" v={`${e.entity?.entity_type || "user"}:${e.entity?.entity_id || e.principal || "—"}`} />
        <Row k="Provider / via" v={`${e.provider} · ${e.via}`} />
        <Row k="Principal" v={e.principal} />
        <Row k="Provenance" v={e.assertion_source || "unattributed"} />
        <Row k="State version" v={`conflict v${e.state_version_at_conflict ?? "—"} · escalated v${e.state_version_at_escalation ?? "—"} · current v${e.current_state_version ?? "—"}`} />
        <Row k="Freshness" v={Object.entries(e.freshness || {}).map(([k, v]) => `${v} ${k}`).join(", ") || "—"} tone="text-emerald-300/85" />
        <Row k="Escalated" v={`${fmt(e.escalated_at)} by ${e.escalated_by || "—"}`} />
        <Row k="Assigned to" v={e.assigned_to || "unassigned"} tone={e.assigned_to ? "text-indigo-200" : "text-amber-300/80"} />
        <Row k="Priority basis" v={e.priority_basis} />
        <Row k="Conflict reason" v={e.conflict_reason || e.escalation_reason || "—"} />
        {e.prior_disposition && (
          <Row k="Disposition" v={`${(e.prior_disposition.outcome || "").toUpperCase()} by ${e.prior_disposition.by} · ${fmt(e.prior_disposition.at)}`} tone="text-emerald-300/85" />
        )}
      </div>

      {!done && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <UserRound className="h-3 w-3 text-white/35" />
          <select
            value={e.assigned_to || ""}
            disabled={busy}
            onChange={(ev) => run(() => nexsysApi.gatewayEscalationAssign(e.id, ev.target.value || null))}
            className="rounded-lg border border-white/10 bg-[#0a0f1a] px-2 py-1 font-mono text-[10px] text-white/80 outline-none focus:border-cyan-400/40"
            data-testid={`escalation-assign-${e.id}`}
          >
            <option value="">— unassigned —</option>
            {reviewers.map((r) => <option key={r.email} value={r.email}>{`${r.name} (${r.email})`}</option>)}
          </select>
          <select
            value={e.priority}
            disabled={busy}
            onChange={(ev) => run(() => nexsysApi.gatewayEscalationPriority(e.id, ev.target.value, reason.trim()))}
            className="rounded-lg border border-white/10 bg-[#0a0f1a] px-2 py-1 font-mono text-[10px] text-white/80 outline-none focus:border-cyan-400/40"
            data-testid={`escalation-priority-${e.id}`}
          >
            {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {e.status === "ASSIGNED" && (
            <button
              onClick={() => run(() => nexsysApi.gatewayEscalationStatus(e.id, "UNDER_REVIEW"))}
              disabled={busy}
              className="flex items-center gap-1 rounded-lg border border-fuchsia-400/30 px-2 py-1 font-mono text-[10px] text-fuchsia-300/90 hover:bg-fuchsia-400/10 disabled:opacity-40"
              data-testid={`escalation-start-review-${e.id}`}
            >
              <Play className="h-2.5 w-2.5" /> Start review
            </button>
          )}
          {e.status === "UNDER_REVIEW" && (
            <button
              onClick={() => run(() => nexsysApi.gatewayEscalationStatus(e.id, "ASSIGNED"))}
              disabled={busy}
              className="rounded-lg border border-white/15 px-2 py-1 font-mono text-[10px] text-white/50 hover:text-white/80 disabled:opacity-40"
              data-testid={`escalation-pause-review-${e.id}`}
            >
              Back to assigned
            </button>
          )}
        </div>
      )}

      {!done && (
        <div className="mt-2 space-y-1.5">
          <input
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            placeholder="Reason / justification (required for dismissal & CRITICAL priority)"
            className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 font-mono text-[10px] text-white/80 outline-none placeholder:text-white/25 focus:border-cyan-400/40"
            data-testid={`escalation-reason-${e.id}`}
          />
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => resolve("accept_as_evidence")}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-lg border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1.5 font-mono text-[10px] text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-40"
              data-testid={`escalation-accept-${e.id}`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Accept as evidence
            </button>
            <button
              onClick={() => resolve("dismiss")}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 font-mono text-[10px] text-white/60 hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-40"
              data-testid={`escalation-dismiss-${e.id}`}
            >
              <Ban className="h-3 w-3" /> Dismiss with reason
            </button>
            <span className="self-center font-mono text-[8.5px] text-white/30">Final disposition flows through the Conflict Resolution Workflow — reviewing state v{e.current_state_version ?? "—"}</span>
          </div>
        </div>
      )}

      {done && e.status === "RESOLVED" && (
        <button
          onClick={() => run(() => nexsysApi.gatewayEscalationStatus(e.id, "CLOSED"), "Escalation CLOSED — full lineage archived.")}
          disabled={busy}
          className="mt-2 flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 font-mono text-[10px] text-white/60 hover:text-white/90 disabled:opacity-40"
          data-testid={`escalation-close-${e.id}`}
        >
          <Archive className="h-3 w-3" /> Close escalation
        </button>
      )}

      <button
        onClick={() => setShowHistory((s) => !s)}
        className="mt-2 flex items-center gap-1 font-mono text-[9px] tracking-widest text-white/35 hover:text-white/70"
        data-testid={`escalation-history-toggle-${e.id}`}
      >
        <History className="h-2.5 w-2.5" /> LINEAGE ({(e.history || []).length}{e.prior_disposition ? " + disposition" : ""})
      </button>
      {showHistory && (
        <div className="mt-1.5 space-y-1 border-l border-white/10 pl-3" data-testid={`escalation-history-${e.id}`}>
          {(e.history || []).map((h, i) => (
            <div key={i} className="font-mono text-[9px] text-white/45">
              <span className="text-cyan-300/70">{h.event}</span> · {h.by} · {fmt(h.at)} · state v{h.state_version ?? "—"}
              {h.detail ? <span className="text-white/35"> — {h.detail}</span> : null}
            </div>
          ))}
          {(e.resolution_trace || []).map((t, i) => (
            <div key={`t${i}`} className="font-mono text-[9px] text-emerald-300/60">
              {t.step}: <span className="text-white/40">{t.detail}</span>
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div className={`mt-1.5 font-mono text-[9.5px] ${msg.tone === "ok" ? "text-emerald-300/90" : "text-amber-300/90"}`} data-testid={`escalation-msg-${e.id}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

export function EscalationQueue({ data, onChanged }) {
  if (!data) return null;
  const queue = data.queue || [];
  return (
    <Panel
      title="Escalation Queue"
      subtitle={`Governed review of escalated conflicts — assignment never changes EDIOS state · reviewers: ${data.reviewer_policy || "admin authority"}`}
      testid="escalation-queue-panel"
    >
      <div className="nx-scroll max-h-[560px] space-y-2.5 overflow-y-auto p-4">
        {queue.length === 0 && (
          <div className="py-4 text-center font-mono text-[11px] text-emerald-300/60" data-testid="escalation-queue-empty">
            Escalation queue is empty — no conflicts awaiting elevated review.
          </div>
        )}
        {queue.map((e) => (
          <EscalationItem key={e.id} e={e} reviewers={data.reviewers || []} onChanged={onChanged} />
        ))}
      </div>
    </Panel>
  );
}
